/**
 * Foundation monitoring — Sentry/GlitchTip init med APP_CONFIG-tagging.
 *
 * Remix-venlig: alle app-specifikke værdier kommer fra APP_CONFIG. Ved remix
 * sættes blot ny VITE_SENTRY_DSN (eller VITE_GLITCHTIP_DSN) i secrets — ingen
 * kode-ændring.
 *
 * GlitchTip er Sentry-API-kompatibel, så samme SDK virker mod begge.
 */
import * as Sentry from "@sentry/react";
import { analyticsAllowed } from "@/lib/cookie-consent";
import { APP_CONFIG } from "@/config/app.config";

// DSN-resolution er fuldt drevet af APP_CONFIG + env-secrets. Foundation
// indeholder ingen app-specifikke `slug === "..."` greninger.
const DSN =
  (import.meta.env.VITE_GLITCHTIP_DSN as string | undefined) ??
  (import.meta.env.VITE_SENTRY_DSN as string | undefined) ??
  APP_CONFIG.monitoring.fallbackDsn;

let initialized = false;

function detectEnvironment(): "production" | "preview" {
  if (typeof window === "undefined") return "preview";
  const host = window.location.hostname;
  if (host === APP_CONFIG.domain || host === `www.${APP_CONFIG.domain}`) {
    return "production";
  }
  if (host === APP_CONFIG.parentDomain || host === `www.${APP_CONFIG.parentDomain}`) {
    return "production";
  }
  return "preview";
}

export function initMonitoring(): void {
  if (initialized) return;
  if (import.meta.env.DEV) return;
  if (!DSN) {
    console.warn(
      `[monitoring] No DSN configured for app "${APP_CONFIG.slug}". ` +
        `Set VITE_GLITCHTIP_DSN to enable error tracking, eller sæt ` +
        `APP_CONFIG.monitoring.fallbackDsn. Se REMIX_SETUP.md sect. B.`,
    );
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: detectEnvironment(),
    release: `${APP_CONFIG.slug}@${import.meta.env.VITE_APP_VERSION ?? "dev"}`,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    initialScope: {
      tags: {
        app_slug: APP_CONFIG.slug,
        app_name: APP_CONFIG.name,
        project_tag: APP_CONFIG.monitoring.projectTag,
      },
    },
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    beforeSend(event) {
      if (!analyticsAllowed()) return null;
      // Normaliser severity-koder (4000=warning, 6000=error) til Sentry-niveauer.
      const sev = (event.extra?.severity ?? event.tags?.severity) as
        | number
        | string
        | undefined;
      if (sev) {
        const n = typeof sev === "string" ? parseInt(sev, 10) : sev;
        if (n === 4000) event.level = "warning";
        else if (n >= 6000) event.level = "error";
      }
      return event;
    },
  });

  initialized = true;

  window.addEventListener("cookieConsentUpdated", () => {
    if (!analyticsAllowed() && initialized) {
      Sentry.close().catch(() => {});
      initialized = false;
    }
  });
}

/**
 * Capture en fejl med standardiseret severity-kode (4000/6000).
 * Bruges af edge-function-fejl der bobler op til UI.
 */
export function captureWithSeverity(
  err: unknown,
  severity: 4000 | 6000,
  context?: Record<string, unknown>,
) {
  if (!initialized) return;
  Sentry.captureException(err, {
    level: severity === 4000 ? "warning" : "error",
    tags: { severity: String(severity), app_slug: APP_CONFIG.slug },
    extra: context,
  });
}

export { Sentry };
